import React, { useState } from 'react';
import { toJS } from 'mobx';
import { observer } from 'mobx-react-lite';
import { Button, Tooltip } from '@insurancemenu/imenu-components-lib';
import { Add } from '@insurancemenu/imenu-components-lib/dist/icons';
import { ReplicatedCustomInfo } from 'types/formService';
import { useRootStore } from 'store/rootStore';
import InfoCardModal from 'components/common/form/custom/ReplicatedInfo/InfoCardModal';
import {
  CustomFieldControl,
  FormController,
  DefaultFormFields,
} from '@insurancemenu/imenu-components-lib/dist/utils/forms';
import { WebFormCustomFields } from 'store/ui-stores/web-form/webFormStore';
import { AlignmentTypes } from '@insurancemenu/imenu-components-lib/dist/types';
import uniqid from 'uniqid';
import InfoCardsTable, { ReplicatedInfoTableColumn } from '../InfoCardsTable';
import { InfoWrapperStyled, ButtonWrapperStyled } from './styles';

interface InputParams {
  addButtonName: string;
  componentName: string;
  amountOfAdditionalCards: number;
  getCustomComponentField: (customField: WebFormCustomFields) => CustomFieldControl<ReplicatedCustomInfo[]>;
  columns: ReplicatedInfoTableColumn[];
  createCustomForm: () => FormController<DefaultFormFields>;
  customModalContent: (customForm: FormController<DefaultFormFields>) => JSX.Element;
  customModalHeaderName: string;
}

/**
 * @deprecated. Use ReplicatedInfoV2 instead.
 */
const InfoCardBlock = observer((params: InputParams): JSX.Element => {
  // Mobx store
  const { webFormStore, customReplicatedInfoStore } = useRootStore();
  customReplicatedInfoStore.setForm(params.componentName, params.createCustomForm());
  const infoFromStore = params.getCustomComponentField(webFormStore.dynamicForm?.customFields as WebFormCustomFields);
  const addedInfoCards: ReplicatedCustomInfo[] = !infoFromStore?.value
    ? []
    : infoFromStore.value?.map((info) => toJS(info)).slice() || [];

  // React state
  const [showInfoCardModal, setShowInfoCardModal] = useState(false);
  const [activeInfoCardId, setActiveInfoCardId] = useState<string | undefined>(undefined);
  const [infoCardModalMode, setInfoCardModalMode] = useState<'ADD' | 'EDIT'>('ADD');
  const [activeInfoCardAnswers, setActiveInfoCardAnswers] = useState<ReplicatedCustomInfo | undefined>(undefined);

  // Component actions
  function openInfoCardModal(): void {
    setShowInfoCardModal(true);
  }

  function closeInfoCardModal(): void {
    setShowInfoCardModal(false);
  }

  function onAddInfoCard(): void {
    setActiveInfoCardId(undefined);
    setActiveInfoCardAnswers(undefined);
    customReplicatedInfoStore.getForm(params.componentName).resetForm();
    setInfoCardModalMode('ADD');
    openInfoCardModal();
  }

  function onEditInfoCard(info: ReplicatedCustomInfo): void {
    setActiveInfoCardId(info.id);
    setActiveInfoCardAnswers(info);
    setInfoCardModalMode('EDIT');
    openInfoCardModal();
  }

  function onDeleteInfoCard(id: string): void {
    const filteredCards = addedInfoCards.filter((info) => info.id !== id);
    infoFromStore.updateValue(filteredCards);
  }

  function onSaveInfoCard(): void {
    if (infoCardModalMode === 'ADD') {
      const newInfoCard: ReplicatedCustomInfo = {
        id: uniqid(),
        ...customReplicatedInfoStore.getForm(params.componentName).getFormDataObject(),
      } as ReplicatedCustomInfo;
      infoFromStore.updateValue([...addedInfoCards, newInfoCard]);
    } else {
      const infoCardUnderUpdate = addedInfoCards.find((depObj: ReplicatedCustomInfo) => depObj.id === activeInfoCardId);
      const indexOfInfoCardUnderUpdate = infoCardUnderUpdate && addedInfoCards.indexOf(infoCardUnderUpdate);
      const newAddedInfoCards = [...addedInfoCards];
      if (infoCardUnderUpdate && indexOfInfoCardUnderUpdate !== undefined) {
        newAddedInfoCards[indexOfInfoCardUnderUpdate] = {
          ...customReplicatedInfoStore.getForm(params.componentName).getFormDataObject(),
          id: infoCardUnderUpdate.id,
        } as ReplicatedCustomInfo;
        infoFromStore.updateValue([...newAddedInfoCards]);
      }
    }
    closeInfoCardModal();
  }

  return (
    <InfoWrapperStyled>
      {/* Don't allow to add more info cards than a certain amount */}
      <ButtonWrapperStyled>
        {addedInfoCards.length >= params.amountOfAdditionalCards ? (
          <Tooltip alignment={AlignmentTypes.BOTTOM_START} label="You have reached the maximum number of allowed items">
            <Button leftIconProp={<Add />} color="primary" size="large" disabled>
              {params.addButtonName}
            </Button>
          </Tooltip>
        ) : (
          <Button leftIconProp={<Add />} color="primary" size="large" onClick={onAddInfoCard}>
            {params.addButtonName}
          </Button>
        )}
      </ButtonWrapperStyled>
      {addedInfoCards?.length > 0 && (
        <InfoCardsTable
          items={addedInfoCards}
          columns={params.columns}
          onEditItem={onEditInfoCard}
          onDeleteItem={onDeleteInfoCard}
        />
      )}
      <InfoCardModal
        show={showInfoCardModal}
        onClose={closeInfoCardModal}
        onAction={onSaveInfoCard}
        mode={infoCardModalMode}
        componentName={params.componentName}
        content={params.customModalContent}
        headerName={params.customModalHeaderName}
        activeInfoCardAnswers={activeInfoCardAnswers}
      />
    </InfoWrapperStyled>
  );
});

export default InfoCardBlock;
